import type { BTreeSpec } from '../model.js';
import { btreeEdgeId, btreeKeyId, btreeNodeId, btreeRecordId } from '../families/btree.js';

export function btreeFixture(width = 640, stress = false, focusNodeId?: string): BTreeSpec {
  return stress ? {
    id: 'btree-stress',
    family: 'btree',
    view: 'schematic',
    viewport: { width, height: 500 },
    rootId: 'root',
    searchKey: 137,
    nodes: [
      { id: 'root', keys: [50, 100], leaf: false },
      { id: 'branch-low', keys: [20], leaf: false },
      { id: 'branch-mid', keys: [70], leaf: false },
      { id: 'branch-high', keys: [130], leaf: false },
      { id: 'leaf-00', keys: [2, 11, 18], leaf: true, records: ['account:002', 'account:011', 'account:018'] },
      { id: 'leaf-01', keys: [20, 31, 44], leaf: true, records: ['account:020', 'account:031', 'account:044'] },
      { id: 'leaf-10', keys: [50, 61, 69], leaf: true, records: ['account:050', 'account:061', 'account:069'] },
      { id: 'leaf-11', keys: [70, 84, 99], leaf: true, records: ['account:070', 'account:084', 'account:099'] },
      { id: 'leaf-20', keys: [100, 117, 129], leaf: true, records: ['account:100', 'account:117', 'account:129'] },
      { id: 'leaf-21', keys: [130, 137, 149], leaf: true, records: ['account:130', 'account:137', 'account:149'] },
    ],
    edges: [
      { from: 'root', to: 'branch-low', range: '(-∞, 50)' },
      { from: 'root', to: 'branch-mid', range: '[50, 100)' },
      { from: 'root', to: 'branch-high', range: '[100, +∞)' },
      { from: 'branch-low', to: 'leaf-00', range: '(-∞, 20)' },
      { from: 'branch-low', to: 'leaf-01', range: '[20, +∞)' },
      { from: 'branch-mid', to: 'leaf-10', range: '(-∞, 70)' },
      { from: 'branch-mid', to: 'leaf-11', range: '[70, +∞)' },
      { from: 'branch-high', to: 'leaf-20', range: '(-∞, 130)' },
      { from: 'branch-high', to: 'leaf-21', range: '[130, +∞)' },
    ],
    ...(focusNodeId ? { focusNodeId } : {}),
    provenance: 'Synthetic B+-tree account index · separators, child ranges, leaf keys, and record evidence are explicit',
  } : {
    id: 'btree',
    family: 'btree',
    view: 'schematic',
    viewport: { width, height: 440 },
    rootId: 'root',
    searchKey: 62,
    nodes: [
      { id: 'root', keys: [40], leaf: false },
      { id: 'branch-left', keys: [15], leaf: false },
      { id: 'branch-right', keys: [60], leaf: false },
      { id: 'leaf-a', keys: [5, 10], leaf: true, records: ['row:005', 'row:010'] },
      { id: 'leaf-b', keys: [15, 25], leaf: true, records: ['row:015', 'row:025'] },
      { id: 'leaf-c', keys: [40, 55], leaf: true, records: ['row:040', 'row:055'] },
      { id: 'leaf-d', keys: [60, 62, 65], leaf: true, records: ['row:060', 'row:062', 'row:065'] },
    ],
    edges: [
      { from: 'root', to: 'branch-left', range: '(-∞, 40)' },
      { from: 'root', to: 'branch-right', range: '[40, +∞)' },
      { from: 'branch-left', to: 'leaf-a', range: '(-∞, 15)' },
      { from: 'branch-left', to: 'leaf-b', range: '[15, +∞)' },
      { from: 'branch-right', to: 'leaf-c', range: '(-∞, 60)' },
      { from: 'branch-right', to: 'leaf-d', range: '[60, +∞)' },
    ],
    ...(focusNodeId ? { focusNodeId } : {}),
    provenance: 'Synthetic B+-tree row index · separators, child ranges, leaf keys, and record evidence are explicit',
  };
}

/** Semantic checkpoints for a guided lookup of the normal fixture's key 62. */
export const btreeGuidedLookupTargets = [
  btreeNodeId('root'),
  btreeEdgeId('root', 'branch-right'),
  btreeNodeId('branch-right'),
  btreeEdgeId('branch-right', 'leaf-d'),
  btreeKeyId('leaf-d', 62),
  btreeRecordId('leaf-d', 62),
] as const;

export const defaultBTreeFocus = 'leaf-d';
